import { useState, useRef, useEffect } from "react";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Upload,
  Trash2,
  Palette,
  Building2,
  Users,
  Shield,
  Save,
  RefreshCw
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SettingsPage = () => {
  const { user, chapter, updateChapter, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";

  const [chapterForm, setChapterForm] = useState({
    name: "",
    header_text: "",
    primary_color: "#CC0000"
  });

  useEffect(() => {
    if (chapter) {
      setChapterForm({
        name: chapter.name || "",
        header_text: chapter.header_text || "",
        primary_color: chapter.primary_color || "#CC0000"
      });
    }
    fetchUsers();
  }, [chapter]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await fetch(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setUsers(await response.json());
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCreateChapter = async () => {
    if (!chapterForm.name.trim()) {
      toast.error("Chapter name is required");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API}/chapters`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(chapterForm)
      });

      if (response.ok) {
        const newChapter = await response.json();
        updateChapter(newChapter);
        
        // Update user with chapter_id
        const userResponse = await fetch(`${API}/auth/me`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ chapter_id: newChapter.chapter_id })
        });
        
        if (userResponse.ok) {
          const updatedUser = await userResponse.json();
          updateUser(updatedUser);
        }
        
        toast.success("Chapter created successfully!");
      } else {
        toast.error("Failed to create chapter");
      }
    } catch (error) {
      toast.error("Error creating chapter");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateChapter = async () => {
    if (!chapter?.chapter_id) return;

    setLoading(true);
    try {
      const response = await fetch(`${API}/chapters/${chapter.chapter_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(chapterForm)
      });

      if (response.ok) {
        const updatedChapter = await response.json();
        updateChapter(updatedChapter);
        toast.success("Settings saved!");
      } else {
        toast.error("Failed to update settings");
      }
    } catch (error) {
      toast.error("Error updating settings");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error("Please upload an image file");
      return;
    }

    if (!chapter?.chapter_id) {
      toast.error("Please create a chapter first");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/chapters/${chapter.chapter_id}/logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        updateChapter({ ...chapter, logo_url: data.logo_url });
        toast.success("Logo uploaded!");
      } else {
        toast.error("Failed to upload logo");
      }
    } catch (error) {
      toast.error("Error uploading logo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!chapter?.chapter_id) return;

    try {
      const response = await fetch(`${API}/chapters/${chapter.chapter_id}/logo`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        updateChapter({ ...chapter, logo_url: null });
        toast.success("Logo removed");
      }
    } catch (error) {
      toast.error("Error removing logo");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await fetch(`${API}/admin/users/${userId}/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success("Role updated");
        fetchUsers();
      } else {
        toast.error("Failed to update role");
      }
    } catch (error) {
      toast.error("Error updating role");
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-900">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your chapter branding and configuration</p>
        </div>

        {/* Chapter Setup / Branding */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" style={{ color: primaryColor }} />
              Chapter Branding
            </CardTitle>
            <CardDescription>
              {chapter ? "Customize your chapter's appearance" : "Create your chapter to get started"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Logo */}
            <div className="flex items-start gap-6">
              <div>
                <Label className="mb-2 block">Chapter Logo</Label>
                <div className="flex items-center gap-4">
                  {chapter?.logo_url ? (
                    <div className="relative">
                      <img 
                        src={`${API.replace('/api', '')}${chapter.logo_url}`}
                        alt="Chapter Logo"
                        className="w-20 h-20 object-contain rounded-lg border"
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6"
                        onClick={handleRemoveLogo}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div 
                      className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-primary transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-8 w-8 text-slate-400" />
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoUpload}
                    data-testid="logo-input"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || !chapter}
                  >
                    {uploading ? "Uploading..." : "Upload Logo"}
                  </Button>
                </div>
              </div>
            </div>

            <Separator />

            {/* Chapter Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="chapter-name">Chapter Name *</Label>
                <Input
                  id="chapter-name"
                  value={chapterForm.name}
                  onChange={(e) => setChapterForm({ ...chapterForm, name: e.target.value })}
                  placeholder="e.g., BNI Champions"
                  data-testid="chapter-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="header-text">Header Text</Label>
                <Input
                  id="header-text"
                  value={chapterForm.header_text}
                  onChange={(e) => setChapterForm({ ...chapterForm, header_text: e.target.value })}
                  placeholder="e.g., Growing Business Through Referrals"
                  data-testid="header-text-input"
                />
              </div>
            </div>

            {/* Primary Color */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Palette className="h-4 w-4" />
                Primary Color
              </Label>
              <div className="flex items-center gap-4">
                <Input
                  type="color"
                  value={chapterForm.primary_color}
                  onChange={(e) => setChapterForm({ ...chapterForm, primary_color: e.target.value })}
                  className="w-16 h-10 p-1 cursor-pointer"
                  data-testid="color-input"
                />
                <Input
                  value={chapterForm.primary_color}
                  onChange={(e) => setChapterForm({ ...chapterForm, primary_color: e.target.value })}
                  placeholder="#CC0000"
                  className="w-32"
                />
                <div className="flex gap-2">
                  {["#CC0000", "#1E40AF", "#047857", "#7C3AED", "#DC2626"].map(color => (
                    <button
                      key={color}
                      className="w-8 h-8 rounded-full border-2 border-white shadow-sm hover:scale-110 transition-transform"
                      style={{ backgroundColor: color }}
                      onClick={() => setChapterForm({ ...chapterForm, primary_color: color })}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              {chapter ? (
                <Button 
                  onClick={handleUpdateChapter}
                  disabled={loading}
                  style={{ backgroundColor: primaryColor }}
                  data-testid="save-settings-btn"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              ) : (
                <Button 
                  onClick={handleCreateChapter}
                  disabled={loading}
                  style={{ backgroundColor: primaryColor }}
                  data-testid="create-chapter-btn"
                >
                  <Building2 className="mr-2 h-4 w-4" />
                  {loading ? "Creating..." : "Create Chapter"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* User Management */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" style={{ color: primaryColor }} />
              User Management
            </CardTitle>
            <CardDescription>
              Manage user roles and permissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingUsers ? (
              <div className="py-8 text-center">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : users.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No users found</p>
            ) : (
              <div className="space-y-4">
                {users.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {u.name?.charAt(0)?.toUpperCase() || "U"}
                      </div>
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {u.user_id === user?.user_id ? (
                        <Badge>You</Badge>
                      ) : (
                        <Select 
                          value={u.role} 
                          onValueChange={(v) => handleRoleChange(u.user_id, v)}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="host">Visitor Host</SelectItem>
                            <SelectItem value="member">Member</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Demo Data section removed for production */}
      </div>
    </Layout>
  );
};

export default SettingsPage;
